import {
  Chip,
  FormControlLabel,
  InputAdornment,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { Flex } from "~/components/Flex";
import { AUTOMATION_SCOPE } from "./constants";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { SecurityPipelineGithubAutomateFormValues } from "./types";
import { useGetWorkspacesQuery } from "~/operations";
import { useScope } from "~/hooks/useScope";
import { Box, Stack } from "@mui/system";
import { SelectMenu } from "~/components/Form/components/SelectMenu/SelectMenu";
import { useState } from "react";
import { Search as SearchIcon } from "@mui/icons-material";

export interface SecurityPipelineGithubAutomateScopeProps {
  isLoading: boolean;
}

export const SecurityPipelineGithubAutomateScope = ({
  isLoading,
}: SecurityPipelineGithubAutomateScopeProps) => {
  const { activeScope } = useScope();
  const { control } =
    useFormContext<SecurityPipelineGithubAutomateFormValues>();
  const watchScope = useWatch({ control, name: "scope" });
  const watchWorkspaceMrns = useWatch({ control, name: "workspaceMrns" });
  const isWorkspaceScope = watchScope === AUTOMATION_SCOPE.WORKSPACE;
  const [anchorEl, setAnchorEl] = useState<null | HTMLInputElement>(null);
  const [isWorkspaceDropdownOpen, setIsWorkspaceDropdownOpen] = useState(false);
  const [searchWorkspace, setSearchWorkspace] = useState("");

  const { data: spaceWorkspacesData } = useGetWorkspacesQuery({
    variables: { input: { scopeMrn: activeScope?.mrn || "skip" } },
    skip: !activeScope?.mrn,
  });
  const spaceWorkspaces =
    spaceWorkspacesData?.workspaces.edges?.map(({ node }) => node) || [];

  const workspaceOptions = spaceWorkspaces.map((workspace) => ({
    label: workspace.name,
    value: workspace.mrn,
  }));

  return (
    <Flex flexDirection="column">
      <Typography fontWeight={700}>Scope</Typography>
      <Controller
        control={control}
        name="scope"
        render={({ field }) => (
          <RadioGroup
            sx={{ ml: 1 }}
            aria-labelledby="scope"
            value={field.value}
            onChange={(e) => field.onChange(e.target.value)}
          >
            <FormControlLabel
              key={AUTOMATION_SCOPE.SPACE}
              control={<Radio disabled={isLoading} />}
              label="Include all assets in this space"
              value={AUTOMATION_SCOPE.SPACE}
              sx={{
                "& .MuiFormControlLabel-label": {
                  fontWeight:
                    field.value === AUTOMATION_SCOPE.SPACE ? 700 : 400,
                },
              }}
            />

            <FormControlLabel
              key={AUTOMATION_SCOPE.WORKSPACE}
              control={<Radio disabled={isLoading} />}
              label="Fix assets by workspace"
              value={AUTOMATION_SCOPE.WORKSPACE}
              sx={{
                "& .MuiFormControlLabel-label": {
                  fontWeight:
                    field.value === AUTOMATION_SCOPE.WORKSPACE ? 700 : 400,
                },
              }}
            />
          </RadioGroup>
        )}
      />
      {isWorkspaceScope && (
        <Box sx={{ ml: 5, mt: 1 }}>
          <TextField
            fullWidth
            disabled={isLoading}
            value={searchWorkspace}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setSearchWorkspace(event.target.value);
            }}
            label="Search to add a Workspace"
            onClick={(event: React.MouseEvent<HTMLInputElement>) => {
              setAnchorEl(event.currentTarget);
              setIsWorkspaceDropdownOpen(true);
            }}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon />
                  </InputAdornment>
                ),
              },
            }}
          />
          <Controller
            disabled={isLoading}
            name="workspaceMrns"
            control={control}
            render={({ field, formState: { errors } }) => (
              <Box sx={{ position: "relative" }}>
                <SelectMenu
                  isOpen={isWorkspaceDropdownOpen}
                  anchorRef={anchorEl}
                  onClose={() => {
                    setAnchorEl(null);
                    setIsWorkspaceDropdownOpen(false);
                  }}
                  maxHeight={208}
                  options={workspaceOptions.filter((option) =>
                    option.label
                      .toLowerCase()
                      .includes(searchWorkspace.toLowerCase()),
                  )}
                  onChange={(value) => {
                    field.onChange(value);
                  }}
                  multiple={true}
                  value={field.value}
                  variant="plain"
                  error={!!errors.workspaceMrns}
                  helperText={errors.workspaceMrns?.message || ""}
                />
                <Stack direction="row" gap={1} mt={1} sx={{ flexWrap: "wrap" }}>
                  {watchWorkspaceMrns.map((workspaceMrn) => (
                    <Chip
                      key={workspaceMrn}
                      label={
                        workspaceOptions.find(
                          (option) => option.value === workspaceMrn,
                        )?.label || ""
                      }
                      onDelete={() =>
                        field.onChange(
                          watchWorkspaceMrns.filter(
                            (mr) => mr !== workspaceMrn,
                          ),
                        )
                      }
                    />
                  ))}
                </Stack>
              </Box>
            )}
          />
        </Box>
      )}
    </Flex>
  );
};
